import React, { useEffect, useRef, useState } from "react";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { loadPublicIce } from "@/lib/iceServers";
import { CallOverlay } from "@/components/messenger/CallOverlay";
import { RING_TIMEOUT_MS, mediaErrorMessage, mediaSupportError } from "@/components/messenger/callHelpers";
import { decideServerError, decideSignal } from "@/components/messenger/callSignals";
import { rideCallStore } from "@/lib/rideCallStore";

export const RIDE_CALL_ACTIVE = ["assigned", "picked_up", "in_transit"];
const RIDE_ERRORS = {
  ride_not_active: "Calls are only available while the trip is active.",
  not_a_ride_participant: "Only the rider and the assigned driver can call each other.",
  ride_not_found: "Trip not found.",
  stale_call: "This call has already ended.",
  already_answered: "This call was answered on another device.",
  participant_changed: "The trip's driver changed, so this call has ended.",
};
const unanswered = (c) => !!c && c.mode !== "ended" && (c.mode === "incoming" || !!c.offer);   // offer is kept until the answer is sent
const newCallId = () => (window.crypto?.randomUUID?.() || `c_${Date.now()}_${Math.random().toString(36).slice(2)}`);

// In-app audio call between a ride's customer and its assigned agent. The server picks the peer from the
// live assignment (type "ride_webrtc"), so this never relies on chat membership.
export function RideCall({ rideId, active, peerName }) {
  const [call, setCall] = useState(null);
  const wsRef = useRef(null), pcRef = useRef(null), localRef = useRef(null), remoteRef = useRef(null);
  const pendingIce = useRef([]), callRef = useRef(null), timer = useRef(null), audioRef = useRef(null);
  const adopted = useRef(null), autoAccept = useRef(false), accepting = useRef(false);
  if (call || callRef.current?.mode !== "starting") callRef.current = call;

  useEffect(() => {
    if (audioRef.current && remoteRef.current && audioRef.current.srcObject !== remoteRef.current) audioRef.current.srcObject = remoteRef.current;
  });

  const send = (signal, callId = callRef.current?.callId) =>
    wsRef.current?.readyState === 1 && (wsRef.current.send(JSON.stringify({ type: "ride_webrtc", ride_id: rideId, call_id: callId, signal })), true);
  const setC = (next) => { callRef.current = next; setCall(next); };

  const cleanup = () => {
    clearTimeout(timer.current);
    localRef.current?.getTracks().forEach((t) => t.stop());
    try { pcRef.current?.close(); } catch {}
    pcRef.current = localRef.current = remoteRef.current = null;
    pendingIce.current = [];
    if (adopted.current) { rideCallStore.clear(rideId, adopted.current.callId); adopted.current = null; }
    autoAccept.current = accepting.current = false;
  };
  const end = (reason = "ended", notify = true) => {
    const c = callRef.current;
    if (!c || c.mode === "ended") return;
    if (notify && c.mode !== "starting") send({ kind: unanswered(c) ? "decline" : "hangup" });
    cleanup();
    setC({ mode: "ended", kind: "audio", reason });
    timer.current = setTimeout(() => setCall((cur) => (cur?.mode === "ended" ? null : cur)), 2500);
  };
  const flush = async () => {
    const pc = pcRef.current;
    while (pc?.remoteDescription && pendingIce.current.length) { try { await pc.addIceCandidate(pendingIce.current.shift()); } catch {} }
  };
  const makePc = async () => {
    const pc = new RTCPeerConnection({ iceServers: await loadPublicIce() });
    pc.onicecandidate = (e) => e.candidate && send({ kind: "ice", candidate: e.candidate.toJSON() });
    pc.ontrack = (e) => { remoteRef.current = e.streams[0] || new MediaStream([e.track]); setCall((c) => (c ? { ...c } : c)); };
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return;
      if (pc.connectionState === "connected") callRef.current = { ...callRef.current, wasConnected: true };
      setCall((c) => (c ? { ...c, net: pc.connectionState, wasConnected: c.wasConnected || pc.connectionState === "connected" } : c));
      if (pc.connectionState === "failed") end(callRef.current?.wasConnected ? "lost" : "failed");
    };
    return pc;
  };
  const media = async () => {
    const bad = mediaSupportError("audio");
    if (bad) throw Object.assign(new Error(bad), { name: "Unsupported", text: bad });
    return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  };

  const start = async () => {
    if (callRef.current && callRef.current.mode !== "ended") return;
    callRef.current = { mode: "starting", kind: "audio", callId: newCallId() };
    try {
      localRef.current = await media();
      const pc = (pcRef.current = await makePc());
      localRef.current.getTracks().forEach((t) => pc.addTrack(t, localRef.current));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      setC({ ...callRef.current, mode: "outgoing", net: "new" });
      send({ kind: "offer", sdp: offer.sdp, callKind: "audio" });
      timer.current = setTimeout(() => callRef.current?.mode === "outgoing" && end("no_answer"), RING_TIMEOUT_MS);
    } catch (e) { cleanup(); callRef.current = null; toast.error(e.text || mediaErrorMessage(e, "audio")); }
  };

  // Idempotent: the banner hand-off (ws.onopen) and a user click can never both run the answer path.
  const accept = async () => {
    const c = callRef.current;
    if (!unanswered(c) || accepting.current) return;
    if (c.mode === "incoming") setC({ ...c, mode: "live", net: "new" });                   // "Connecting…", Accept hidden
    if (wsRef.current?.readyState !== 1) { autoAccept.current = true; return; }      // answer once the socket is open
    autoAccept.current = false;
    accepting.current = true;
    clearTimeout(timer.current);
    try {
      localRef.current = await media();
      const pc = (pcRef.current = await makePc());
      localRef.current.getTracks().forEach((t) => pc.addTrack(t, localRef.current));
      await pc.setRemoteDescription({ type: "offer", sdp: c.offer });
      if (adopted.current) { pendingIce.current = [...adopted.current.ice, ...pendingIce.current]; rideCallStore.clear(rideId, c.callId); adopted.current = null; }
      await flush();
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      if (!send({ kind: "answer", sdp: ans.sdp })) throw Object.assign(new Error("ws"), { text: "Connection lost, so the call could not be answered." });
      accepting.current = false;
      setC({ ...callRef.current, mode: "live", offer: undefined, net: pc.connectionState });
    } catch (e) {
      accepting.current = false;
      toast.error(e.text || mediaErrorMessage(e, "audio"));
      end(e.message === "ws" ? "failed" : "media");
    }
  };

  const onSignal = async (msg) => {
    const pc = pcRef.current, sig = msg.signal || {};
    const curId = callRef.current?.mode !== "ended" && callRef.current?.callId;
    if (sig.kind !== "offer" && curId && msg.call_id !== curId) return;                 // late signal of an older call
    if (sig.kind === "offer" && !sig.renegotiate && curId && msg.call_id === curId) return;   // server replay of the call we hold
    if (sig.kind === "hangup" && RIDE_ERRORS[sig.reason]) toast.error(RIDE_ERRORS[sig.reason]);
    const d = decideSignal({ ...msg, conv_id: rideId }, { threadId: rideId, cur: callRef.current,
      pc: { exists: !!pc, hasRemote: !!pc?.remoteDescription, signalingState: pc?.signalingState } });
    if (d.type === "busy") send({ kind: "busy" });
    else if (d.type === "incoming") {
      pendingIce.current = [];
      setC({ ...d.call, callId: msg.call_id });
      send({ kind: "ringing" });
      timer.current = setTimeout(() => unanswered(callRef.current) && end("missed", false), RING_TIMEOUT_MS + 5000);
    } else if (d.type === "ringing") setCall((c) => (c?.mode === "outgoing" ? { ...c, ringing: true } : c));
    else if (d.type === "answer") {
      clearTimeout(timer.current);
      try { await pc.setRemoteDescription({ type: "answer", sdp: sig.sdp }); await flush(); setC({ ...callRef.current, mode: "live", net: pc.connectionState }); }
      catch { end("failed"); }
    } else if (d.type === "ice_add") { try { await pc.addIceCandidate(sig.candidate); } catch {} }
    else if (d.type === "ice_buffer") pendingIce.current.push(sig.candidate);
    else if (d.type === "end") end(d.reason, false);
  };

  useEffect(() => {
    let stopped = false, ws;
    const unmount = rideCallStore.mount(rideId);
    const p = rideCallStore.take(rideId);                    // call handed over from the global banner
    if (p) {
      adopted.current = p;
      autoAccept.current = p.autoAccept;
      setC({ mode: p.autoAccept ? "live" : "incoming", net: "new", kind: "audio", from: p.from, offer: p.offer, callId: p.callId });
      const left = Math.max(1000, RING_TIMEOUT_MS + 5000 - (Date.now() - p.at));
      timer.current = setTimeout(() => unanswered(callRef.current) && end("missed", false), left);
    }
    (async () => {
      let ticket = "";
      try { ticket = (await api.get("/messenger/ws-ticket")).data.ticket; } catch {}
      if (stopped) return;
      ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/messenger/ws?ticket=${ticket}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        if (m.ride_id !== rideId) return;
        if (m.type === "ride_webrtc") onSignal(m);
        else if (m.type === "ride_call_claimed") {
          const c = callRef.current;
          if (unanswered(c) && c.callId === m.call_id) { cleanup(); setC(null); }
        } else if (m.type === "ride_webrtc_error") {
          const c = callRef.current;
          if (m.call_id && c?.callId && m.call_id !== c.callId) return;
          const d = decideServerError(m.reason, c);
          if (RIDE_ERRORS[m.reason]) toast.error(RIDE_ERRORS[m.reason]);
          if (d.type === "end") end(d.reason, false);
          else if (c?.mode === "live" && !c.wasConnected && ["stale_call", "already_answered"].includes(m.reason)) end("failed", false);
        }
      };
      ws.onopen = () => { if (autoAccept.current) { autoAccept.current = false; accept(); } };
    })();
    const onHide = () => { const c = callRef.current; if (c && c.mode !== "ended" && c.mode !== "starting") send({ kind: unanswered(c) ? "decline" : "hangup" }); };
    window.addEventListener("pagehide", onHide);
    return () => {
      stopped = true;
      // Not answered yet (StrictMode remount or navigation): give it back to the banner. callRef is nulled so end() below
      // neither declines nor clears the store entry.
      if (adopted.current && unanswered(callRef.current) && !accepting.current) {
        rideCallStore.release(rideId, adopted.current);
        adopted.current = null;
        clearTimeout(timer.current);
        callRef.current = null;
        setCall(null);
      }
      unmount(); window.removeEventListener("pagehide", onHide); end("ended"); ws?.close(1000);
    };
  }, [rideId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {active && (
        <button onClick={start} disabled={!!call && call.mode !== "ended"} data-testid="ride-voip"
                className="mt-2 w-full text-xs font-bold rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-2 inline-flex items-center justify-center gap-1">
          <PhoneCall size={12} /> In-app voice call (no number shared)
        </button>
      )}
      {call && (
        <CallOverlay call={call} name={peerName} refs={{ remoteAudio: audioRef, remoteVideo: { current: null }, localVideo: { current: null } }}
                     onAccept={accept} onDecline={() => end("declined")} onEnd={() => end("ended")} screenSharing={false} onToggleShare={() => {}} allowScreenShare={false} />
      )}
    </>
  );
}
